import React, { useEffect, useRef } from "react";
import { useMediaQuery } from "@mui/material";
import { Box } from "@mui/system";
import CloseIcon from "@mui/icons-material/Close";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useBanner } from "../../Context/BannerContext";
import { Capacitor } from "@capacitor/core";

const BANNER_KEY = "soc2_banner_dismissed";
const RESURFACE_CHANCE = 0;

// Catches native iOS app AND iPhone/iPad Safari/Chrome
const isIOSDevice = () =>
  Capacitor.getPlatform() === "ios" ||
  /iPad|iPhone|iPod/.test(navigator.userAgent);

export const Soc2Banner = () => {
  const { isBannerVisible, hideBanner, updateBannerHeight } = useBanner();
  const isSmall = useMediaQuery("(max-width:600px)");
  const bannerRef = useRef(null);

  // Always call hooks first, then bail — also ensure height is zeroed out
  useEffect(() => {
    if (isIOSDevice()) {
      updateBannerHeight(0);
    }
  }, []);

  useEffect(() => {
    if (bannerRef.current && isBannerVisible) {
      updateBannerHeight(bannerRef.current.offsetHeight);
    }
  }, [isBannerVisible, isSmall]);

  if (isIOSDevice()) return null;

  const wasDismissed = localStorage.getItem(BANNER_KEY) === "true";
  const shouldShow = !wasDismissed || Math.random() < RESURFACE_CHANCE;

  if (!isBannerVisible || !shouldShow) return null;

  const handleClose = () => {
    localStorage.setItem(BANNER_KEY, "true");
    updateBannerHeight(0);
    hideBanner();
  };

  return (
    <Box
      ref={bannerRef}
      sx={{
        background:
          "linear-gradient(135deg, #1a7a4a 0%, #22a05a 50%, #1a7a4a 100%)",
        backgroundSize: "200% 200%",
        animation: "soc2shimmer 5s ease infinite",
        "@keyframes soc2shimmer": {
          "0%": { backgroundPosition: "0% 50%" },
          "50%": { backgroundPosition: "100% 50%" },
          "100%": { backgroundPosition: "0% 50%" },
        },
        width: "100%",
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 99999,
        minHeight: { xs: "52px", sm: "48px" },
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: { xs: 1.5, sm: 2 },
        gap: { xs: 1, sm: 1.5 },
      }}
    >
      <LockOutlinedIcon
        sx={{
          fontSize: { xs: 15, sm: 16 },
          color: "rgba(255,255,255,0.85)",
          flexShrink: 0,
        }}
      />

      <Box
        component="span"
        sx={{
          fontSize: { xs: "12px", sm: "13.5px" },
          color: "rgba(255,255,255,0.95)",
          fontWeight: 400,
          letterSpacing: "0.01em",
          lineHeight: 1.4,
          textAlign: "center",
          flex: 1,
          py: { xs: "10px", sm: "12px" },
        }}
      >
        {isSmall ? (
          <>
            <strong style={{ fontWeight: 600 }}>SOC 2 in progress</strong> —
            your data is safe.
          </>
        ) : (
          <>
            <strong style={{ fontWeight: 600 }}>
              SOC 2 compliance in progress
            </strong>{" "}
            — we're committed to keeping your data secure and private.
          </>
        )}
      </Box>

      <Box
        component="a"
        href="https://trust.delve.co/blitz"
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          background: "rgba(255,255,255,0.15)",
          border: "1px solid rgba(255,255,255,0.3)",
          color: "white",
          fontSize: { xs: "11.5px", sm: "12px" },
          fontWeight: 600,
          px: { xs: "10px", sm: "14px" },
          py: "5px",
          borderRadius: "20px",
          textDecoration: "none",
          whiteSpace: "nowrap",
          flexShrink: 0,
          letterSpacing: "0.02em",
          transition: "all 0.15s ease",
          "&:hover": {
            background: "rgba(255,255,255,0.25)",
            borderColor: "rgba(255,255,255,0.5)",
          },
        }}
      >
        {isSmall ? "View ↗" : "View Report ↗"}
      </Box>

      <Box
        component="button"
        onClick={handleClose}
        sx={{
          width: { xs: "36px", sm: "32px" },
          height: { xs: "36px", sm: "32px" },
          borderRadius: "50%",
          background: "rgba(255,255,255,0.12)",
          border: "none",
          color: "rgba(255,255,255,0.85)",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          transition: "all 0.15s ease",
          ml: "4px",
          "&:hover": {
            background: "rgba(255,255,255,0.22)",
            color: "white",
          },
        }}
        aria-label="Dismiss banner"
      >
        <CloseIcon sx={{ fontSize: { xs: 16, sm: 15 } }} />
      </Box>
    </Box>
  );
};